// frontend/src/services/supplierService.ts
// Service layer: maps backend Supplier/PurchaseOrder ↔ frontend commandes types

import api from './api';
import type {
  Supplier as BackendSupplier,
  PurchaseOrder as BackendPO,
  PurchaseOrderItem as BackendPOItem,
  Delivery as BackendDelivery,
} from '../types/models';
import type {
  Supplier as FrontendSupplier,
  Order as FrontendOrder,
  OrderItem as FrontendOrderItem,
} from '../components/commandes/commandesTypes';

// =============================================================================
// Adapters: Backend → Frontend
// =============================================================================

/** Map backend Supplier to frontend Supplier shape */
export function backendToFrontendSupplier(s: BackendSupplier): FrontendSupplier {
  return {
    id: s.id,
    nom: s.name,
    code: s.code || undefined,
    email: s.email || '',
    telephone: s.phone || '',
    contact: s.contactName || undefined,
    fax: s.fax || undefined,
    website: s.website || undefined,
    adresse: s.address || undefined,
    city: s.city || undefined,
    province: s.province || undefined,
    postalCode: s.postalCode || undefined,
    country: s.country || undefined,
    paymentTerms: s.paymentTerms || undefined,
    currency: s.currency || undefined,
    jourCommande: s.orderDay || undefined,
    delaiLivraison: s.defaultLeadTimeDays ?? undefined,
    minimumCommande: s.minOrderAmount != null ? Number(s.minOrderAmount) : undefined,
    coverImage: s.coverImage || undefined,
    notes: s.notes || undefined,
    categories: [],
    resourceCount: s._count?.resources ?? undefined,
    resourceNames: Array.isArray(s.resources)
      ? s.resources.map((r: any) => ({
          name: r.resource?.name ?? '?',
          unitCost: r.unitCost != null ? Number(r.unitCost) : null,
        }))
      : undefined,
  };
}

/** Map backend PurchaseOrder to frontend Order shape */
export function backendToFrontendOrder(po: BackendPO): FrontendOrder {
  const statusMap: Record<string, FrontendOrder['status']> = {
    DRAFT: 'brouillon',
    SUBMITTED: 'envoyee',
    CONFIRMED: 'envoyee',
    PARTIAL: 'recue',
    RECEIVED: 'recue',
    CANCELLED: 'brouillon',
  };

  const items: FrontendOrderItem[] = (po.items || []).map((item: BackendPOItem) => ({
    productId: item.resourceId,
    productName: item.resource?.name || item.resourceId,
    quantity: item.quantity,
    currentStock: Number(item.resource?.currentQuantity) || 0,
    unitPrice: Number(item.unitCost) || 0,
    orderedQuantity: item.quantity,
  }));

  return {
    id: po.id,
    supplierId: po.supplierId,
    items,
    status: statusMap[po.status] || 'brouillon',
    dateCreation: new Date(po.orderDate),
    dateEnvoi: po.status !== 'DRAFT' ? new Date(po.updatedAt) : undefined,
    dateLivraisonEstimee: po.expectedDate ? new Date(po.expectedDate) : undefined,
    total: po.totalAmount,
    notes: po.notes || undefined,
  };
}

// =============================================================================
// Adapters: Frontend → Backend
// =============================================================================

/** Map frontend Supplier fields to backend create/update payload */
export function frontendToBackendSupplier(s: Partial<FrontendSupplier>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (s.nom !== undefined) payload.name = s.nom;
  if (s.code !== undefined) payload.code = s.code || null;
  if (s.email !== undefined) payload.email = s.email || null;
  if (s.telephone !== undefined) payload.phone = s.telephone || null;
  if (s.contact !== undefined) payload.contactName = s.contact || null;
  if (s.fax !== undefined) payload.fax = s.fax || null;
  if (s.website !== undefined) payload.website = s.website || null;
  if (s.adresse !== undefined) payload.address = s.adresse || null;
  if (s.city !== undefined) payload.city = s.city || null;
  if (s.province !== undefined) payload.province = s.province || null;
  if (s.postalCode !== undefined) payload.postalCode = s.postalCode || null;
  if (s.country !== undefined) payload.country = s.country || null;
  if (s.paymentTerms !== undefined) payload.paymentTerms = s.paymentTerms || null;
  if (s.currency !== undefined) payload.currency = s.currency || null;
  if (s.jourCommande !== undefined) payload.orderDay = s.jourCommande || null;
  if (s.delaiLivraison !== undefined) payload.defaultLeadTimeDays = s.delaiLivraison ?? null;
  if (s.minimumCommande !== undefined) payload.minOrderAmount = s.minimumCommande ?? null;
  if (s.coverImage !== undefined) payload.coverImage = s.coverImage || null;
  if (s.notes !== undefined) payload.notes = s.notes || null;
  return payload;
}

// =============================================================================
// API Calls
// =============================================================================

export async function fetchSuppliers(params?: {
  search?: string;
  isActive?: boolean;
}): Promise<BackendSupplier[]> {
  const response = await api.get('/suppliers', { params });
  const data = response.data.data;
  return data.suppliers;
}

export async function fetchSupplier(id: string): Promise<BackendSupplier> {
  const response = await api.get(`/suppliers/${id}`);
  return response.data.data;
}

export async function createSupplier(data: Record<string, unknown>): Promise<BackendSupplier> {
  const response = await api.post('/suppliers', data);
  return response.data.data;
}

export async function updateSupplier(id: string, data: Record<string, unknown>): Promise<BackendSupplier> {
  const response = await api.put(`/suppliers/${id}`, data);
  return response.data.data;
}

/** Soft-delete (deactivate) a supplier. Backend sets isActive=false. */
export async function deleteSupplier(id: string): Promise<void> {
  await api.delete(`/suppliers/${id}`);
}

export async function fetchPurchaseOrders(params?: {
  status?: string;
  supplierId?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<BackendPO[]> {
  const response = await api.get('/purchase-orders', { params });
  const data = response.data.data;
  return data.orders;
}

export async function fetchPurchaseOrder(id: string): Promise<BackendPO> {
  const response = await api.get(`/purchase-orders/${id}`);
  return response.data.data;
}

export async function createPurchaseOrder(data: {
  supplierId: string;
  expectedDate?: string;
  notes?: string;
  items: { resourceId: string; quantity: number; unitCost: number }[];
}): Promise<BackendPO> {
  const response = await api.post('/purchase-orders', data);
  return response.data.data;
}

export async function submitPurchaseOrder(id: string): Promise<BackendPO> {
  const response = await api.post(`/purchase-orders/${id}/submit`);
  return response.data.data;
}

export async function fetchDeliveries(params?: {
  status?: string;
  supplierId?: string;
}): Promise<BackendDelivery[]> {
  const response = await api.get('/deliveries', { params });
  const data = response.data.data;
  return data.deliveries;
}
